import {
  MCPServerAuthApiKeyStore,
  MCPServerAuthType,
  MCPServerAuthApiKey,
  MCPServerTransport,
  ToolsetModel,
  ToolsetSettingsType,
  ToolsetState,
  ELMA365AppElementToolFeedLoadMode,
} from '../../../../../gamma-api';

export const MAIN_CLASS_NAME = 'agent-toolsets-tab';
export const EMPTY_CONTENT_CLASS_NAME = `${MAIN_CLASS_NAME}__empty-content`;
export const EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME = `${EMPTY_CONTENT_CLASS_NAME}__placeholder`;
export const SKELETON_CONTAINER_CLASS_NAME = `${MAIN_CLASS_NAME}__skeleton-container`;

export const SKELETON_HEIGHT = 50;
export const SKELETON_BORDER_RADIUS = 16;

export const TOOLSETS_PAGE_SIZE = 30;

const DEFAULT_FETCH_URL_TIMEOUT_SECONDS = 10;
const DEFAULT_MAX_CONTENT_TOKENS = 20000;
const DEFAULT_FEED_MAX_CONTENT_TOKENS = 20000;
const DEFAULT_SMART_SEARCH_DOWNLOAD_DOCUMENT_MAX_CONTENT_TOKENS = 20000;

export const EMPTY_TOOLSET_MCP_SERVER: ToolsetModel = {
  toolset_id: '',
  folder_id: '',
  created_on: '',
  modified_on: '',
  agent_id: '',
  state: ToolsetState.Enabled,
  type: ToolsetSettingsType.McpServer,
  settings: {
    name: '',
    toolset_type: ToolsetSettingsType.McpServer,
    server_transport: MCPServerTransport.Streamable,
    server_url: '',
    server_auth: {
      auth_type: MCPServerAuthType.NoAuth,
    },
    http_headers: [],
  },
};

export const EMPTY_TOOLSET_DATE_TIME: ToolsetModel = {
  toolset_id: '',
  folder_id: '',
  created_on: '',
  modified_on: '',
  agent_id: '',
  state: ToolsetState.Enabled,
  type: ToolsetSettingsType.DateTime,
  settings: {
    toolset_type: ToolsetSettingsType.DateTime,
    get_current_datetime_tool_settings: {},
  },
};

export const EMPTY_TOOLSET_SMART_SEARCH: ToolsetModel = {
  toolset_id: '',
  folder_id: '',
  created_on: '',
  modified_on: '',
  agent_id: '',
  state: ToolsetState.Enabled,
  type: ToolsetSettingsType.SmartSearch,
  settings: {
    toolset_type: ToolsetSettingsType.SmartSearch,
    project_id: '',
    sources: [],
    query_tool_settings: {
      enabled: false,
    },
    search_documents_tool_settings: {
      enabled: false,
    },
    download_document_tool_settings: {
      enabled: false,
      max_content_token_count: DEFAULT_SMART_SEARCH_DOWNLOAD_DOCUMENT_MAX_CONTENT_TOKENS,
    },
  },
};

export const EMPTY_TOOLSET_WEB: ToolsetModel = {
  toolset_id: '',
  folder_id: '',
  created_on: '',
  modified_on: '',
  agent_id: '',
  state: ToolsetState.Enabled,
  type: ToolsetSettingsType.Web,
  settings: {
    toolset_type: ToolsetSettingsType.Web,
    fetch_url_tool_settings: {
      fetch_timeout_seconds: DEFAULT_FETCH_URL_TIMEOUT_SECONDS,
      max_content_token_count: DEFAULT_MAX_CONTENT_TOKENS,
      allowed_hosts: [],
    },
  },
};

export const EMPTY_TOOLSET_ELMA365_GENERAL: ToolsetModel = {
  toolset_id: '',
  folder_id: '',
  created_on: '',
  modified_on: '',
  agent_id: '',
  state: ToolsetState.Enabled,
  type: ToolsetSettingsType.Elma365General,
  settings: {
    toolset_type: ToolsetSettingsType.Elma365General,
    connector_id: '',
    tool_names_prefix: '',
    fetch_app_element_tool_settings: {
      enabled: true,
      feed_load_mode: ELMA365AppElementToolFeedLoadMode.MetadataOnly,
    },
    fetch_app_feed_tool_settings: {
      enabled: true,
      max_content_token_count: DEFAULT_FEED_MAX_CONTENT_TOKENS,
    },
    read_file_tool_settings: {
      enabled: true,
      max_content_token_count: DEFAULT_MAX_CONTENT_TOKENS,
    },
    describe_image_tool_settings: {
      enabled: false,
      llm_settings: {},
      allow_prompt_override: true,
    },
    fetch_current_user_tool_settings: {
      enabled: true,
    },
    search_elma365_tasks_tool_settings: {
      enabled: false,
    },
    create_elma365_task_tool_settings: {
      enabled: false,
    },
    fetch_elma365_task_tool_settings: {
      enabled: false,
    },
    reassign_elma365_task_tool_settings: {
      enabled: false,
    },
    update_elma365_task_dates_tool_settings: {
      enabled: false,
    },
    update_elma365_task_percent_tool_settings: {
      enabled: false,
    },
    fetch_docflow_lists_tool_settings: {
      enabled: false,
    },
  },
};

export const EMPTY_TOOLSET_ELMA365_TOOLKIT: ToolsetModel = {
  toolset_id: '',
  folder_id: '',
  created_on: '',
  modified_on: '',
  agent_id: '',
  state: ToolsetState.Enabled,
  type: ToolsetSettingsType.Elma365Toolkit,
  settings: {
    toolset_type: ToolsetSettingsType.Elma365Toolkit,
    toolkit_id: '',
    connector_id: null,
    tool_names_prefix: '',
  },
};

export const EMPTY_TOOLSET_FILE: ToolsetModel = {
  toolset_id: '',
  folder_id: '',
  created_on: '',
  modified_on: '',
  agent_id: '',
  state: ToolsetState.Enabled,
  type: ToolsetSettingsType.File,
  settings: {
    toolset_type: ToolsetSettingsType.File,
    read_file_tool_settings: {
      enabled: true,
      max_content_token_count: DEFAULT_MAX_CONTENT_TOKENS,
    },
    write_file_tool_settings: {
      enabled: true,
    },
    describe_image_tool_settings: {
      enabled: false,
      llm_settings: {},
      allow_prompt_override: true,
    },
  },
};

export const EMPTY_TOOLSET_AGENT: ToolsetModel = {
  toolset_id: '',
  folder_id: '',
  created_on: '',
  modified_on: '',
  agent_id: '',
  state: ToolsetState.Enabled,
  type: ToolsetSettingsType.Agent,
  settings: {
    toolset_type: ToolsetSettingsType.Agent,
    agent_id: '',
    call_agent_tool_settings: {},
  },
};

export const getEmptyToolsetForType = (type?: ToolsetSettingsType): ToolsetModel => {
  switch (type) {
    case ToolsetSettingsType.McpServer:
      return EMPTY_TOOLSET_MCP_SERVER;
    case ToolsetSettingsType.DateTime:
      return EMPTY_TOOLSET_DATE_TIME;
    case ToolsetSettingsType.SmartSearch:
      return EMPTY_TOOLSET_SMART_SEARCH;
    case ToolsetSettingsType.Web:
      return EMPTY_TOOLSET_WEB;
    case ToolsetSettingsType.File:
      return EMPTY_TOOLSET_FILE;
    case ToolsetSettingsType.Agent:
      return EMPTY_TOOLSET_AGENT;
    case ToolsetSettingsType.Elma365General:
      return EMPTY_TOOLSET_ELMA365_GENERAL;
    case ToolsetSettingsType.Elma365Toolkit:
      return EMPTY_TOOLSET_ELMA365_TOOLKIT;
    default:
      return EMPTY_TOOLSET_MCP_SERVER;
  }
};

export const EMPTY_API_KEY_AUTH_SETTINGS: MCPServerAuthApiKey = {
  api_key_name: 'X-Token',
  api_key_store: MCPServerAuthApiKeyStore.Header,
  api_key_value: '',
};

export const getEmptyAuthSettingsForType = (type?: MCPServerAuthType): MCPServerAuthApiKey | null => {
  switch (type) {
    case MCPServerAuthType.ApiKey:
      return EMPTY_API_KEY_AUTH_SETTINGS;
    case MCPServerAuthType.NoAuth:
      return null;
    default:
      return null;
  }
};
